import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID
} from '@angular/core';

import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { finalize, forkJoin, Subscription } from 'rxjs';

import { AhmedElshahatJobService } from '../../../../services/ahmed-elshahat-job';
import { CountryCityService } from '../../../../services/country-city.service';
import { JobApplicationService } from '../../../../services/job-application';

type FilterKey =
  | 'search'
  | 'country_id'
  | 'city_id'
  | 'work_model'
  | 'employment_type';

interface JobsFilters {
  search: string;
  country_id: string;
  city_id: string;
  work_model: string;
  employment_type: string;
}

const UI_LABELS = {
  en: {
    heroEyebrow: 'Open opportunities',
    heroTitle: 'Find Your Dream Job',
    heroSubtitle: 'Discover opportunities from leading companies and apply in a few clicks.',
    searchPlaceholder: 'Job title, keyword, or description',
    allCountries: 'City or Country',
    searchJobs: 'Search Jobs',
    filters: 'Filters',
    resetAll: 'Reset All',
    jobType: 'Job Type',
    allJobTypes: 'All job types',
    workModel: 'Work Model',
    allWorkModels: 'All work models',
    location: 'Location',
    country: 'Country',
    city: 'City',
    allCities: 'All cities',
    latestOpportunities: 'Latest Opportunities',
    jobsFound: 'jobs found',
    shareSearch: 'Share Search',
    loading: 'Loading jobs...',
    emptyTitle: 'No jobs found',
    emptyText: 'Try changing the search or clearing filters.',
    details: 'View Details',
    apply: 'Apply',
    salary: 'Salary',
    postedAt: 'Posted At',
    views: 'Views',
    employmentType: 'Employment Type',
    examRequired: 'Exam Required',
    personalityAssessment: 'Personality Assessment',
    yes: 'Yes',
    no: 'No',
    description: 'Description',
    responsibilities: 'Responsibilities',
    skills: 'Skills',
    requirements: 'Requirements',
    required: 'Required',
    close: 'Close',
    applyNow: 'Apply Now',
    recently: 'Recently',
    yesterday: 'Yesterday',
    dayAgo: 'day ago',
    daysAgo: 'days ago',
    copied: 'Search link copied',
    confidentialEmployer: 'Confidential Employer',
    notSpecified: 'Not specified',
    untitledJob: 'Untitled Job',
    salaryNotMentioned: 'Salary not mentioned',
    competitiveSalary: 'Competitive salary',
    negotiable: 'Negotiable',
    from: 'From',
    upTo: 'Up to',
    published: 'Published',
    draft: 'Draft',
    closed: 'Closed'
  },
  ar: {
    heroEyebrow: 'فرص متاحة',
heroTitle: 'خطوتك الأولى نحو فرصة عمل عالمية',
    heroSubtitle: 'اكتشف فرص عمل من شركات مميزة وقدّم بسهولة في خطوة واحدة.',
    searchPlaceholder: 'اسم الوظيفة أو كلمة مفتاحية أو وصف',
    allCountries: 'المدينة أو الدولة',
    searchJobs: 'ابحث عن الوظائف',
    filters: 'الفلاتر',
    resetAll: 'مسح الكل',
    jobType: 'نوع الوظيفة',
    allJobTypes: 'كل الأنواع',
    workModel: 'نظام العمل',
    allWorkModels: 'كل أنظمة العمل',
    location: 'المكان',
    country: 'الدولة',
    city: 'المدينة',
    allCities: 'كل المدن',
    latestOpportunities: 'أحدث الفرص',
    jobsFound: 'وظيفة',
    shareSearch: 'مشاركة البحث',
    loading: 'جاري تحميل الوظائف...',
    emptyTitle: 'لا توجد وظائف',
    emptyText: 'جرّب تغيير البحث أو مسح الفلاتر.',
    details: 'التفاصيل',
    apply: 'قدّم الآن',
    salary: 'الراتب',
    postedAt: 'تاريخ النشر',
    views: 'المشاهدات',
    employmentType: 'نوع الدوام',
    examRequired: 'اختبار مطلوب',
    personalityAssessment: 'تقييم شخصية',
    yes: 'نعم',
    no: 'لا',
    description: 'الوصف',
    responsibilities: 'المسؤوليات',
    skills: 'المهارات',
    requirements: 'المتطلبات',
    required: 'مطلوب',
    close: 'إغلاق',
    applyNow: 'قدّم الآن',
    recently: 'حديثًا',
    yesterday: 'أمس',
    dayAgo: 'يوم',
    daysAgo: 'أيام',
    copied: 'تم نسخ رابط البحث',
    confidentialEmployer: 'صاحب عمل سري',
    notSpecified: 'غير محدد',
    untitledJob: 'وظيفة بدون عنوان',
    salaryNotMentioned: 'الراتب غير مذكور',
    competitiveSalary: 'راتب تنافسي',
    negotiable: 'قابل للتفاوض',
    from: 'يبدأ من',
    upTo: 'حتى',
    published: 'منشورة',
    draft: 'مسودة',
    closed: 'مغلقة'
  }
} as const;

type LabelKey = keyof typeof UI_LABELS.en;

@Component({
  selector: 'app-jobsfullpage',
  templateUrl: './jobsfullpage.component.html',
  styleUrl: './jobsfullpage.component.scss'
})
export class JobsfullpageComponent implements OnInit, OnDestroy {
  private isBrowser = false;
  private subscriptions = new Subscription();

  contentReady = false;

  readonly perPage = 12;

  jobs: any[] = [];
  countries: any[] = [];
  cities: any[] = [];

  totalJobsCount = 0;
  currentPage = 1;
  lastPage = 1;
  from = 0;
  to = 0;

  isLoading = false;
  isDetailsLoading = false;

  selectedJob: any = null;
  modalOpen = false;

  selectedApplyJob: any = null;
  applyModalOpen = false;
  directApplyingJobId: number | string | null = null;
  appliedJobIds = new Set<string>();

  shareMessage = '';

  filtersModalOpen = false;

  filters: JobsFilters = {
    search: '',
    country_id: '',
    city_id: '',
    work_model: '',
    employment_type: ''
  };

  mobileFilters: JobsFilters = {
    search: '',
    country_id: '',
    city_id: '',
    work_model: '',
    employment_type: ''
  };

  employmentTypeOptions = [
    { value: 'full_time', labelEn: 'Full-time', labelAr: 'دوام كامل' },
    { value: 'part_time', labelEn: 'Part-time', labelAr: 'دوام جزئي' },
    { value: 'contract', labelEn: 'Contract', labelAr: 'تعاقد' },
    { value: 'internship', labelEn: 'Internship', labelAr: 'تدريب' }
  ];

  workModelOptions = [
    { value: 'on_site', labelEn: 'On site', labelAr: 'من مقر العمل' },
    { value: 'remote', labelEn: 'Remote', labelAr: 'عن بُعد' },
    { value: 'hybrid', labelEn: 'Hybrid', labelAr: 'هايبرد' }
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private jobService: AhmedElshahatJobService,
    private countryCityService: CountryCityService,
    private applicationService: JobApplicationService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    if (!this.isBrowser) {
      return;
    }

    this.contentReady = true;
    this.getCountriesAndCities();
    this.loadAppliedJobIds();

    const querySub = this.route.queryParamMap.subscribe((params: ParamMap) => {
      this.hydrateFiltersFromQuery(params);
      this.getJobs(this.currentPage);
    });

    this.subscriptions.add(querySub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get isAr(): boolean {
    return this.getCurrentLang().startsWith('ar');
  }

  get filteredCities(): any[] {
    if (!this.filters.country_id) {
      return this.cities;
    }

    return this.cities.filter((city: any) => {
      return String(city.country_id) === String(this.filters.country_id);
    });
  }

  get mobileFilteredCities(): any[] {
    if (!this.mobileFilters.country_id) {
      return this.cities;
    }

    return this.cities.filter((city: any) => {
      return String(city.country_id) === String(this.mobileFilters.country_id);
    });
  }

  get hasFilters(): boolean {
    return this.hasAnyFilter(this.filters);
  }

  get hasMobileFilters(): boolean {
    return this.hasAnyFilter(this.mobileFilters);
  }

  get pages(): number[] {
    const maxButtons = 5;

    let start = Math.max(1, this.currentPage - 2);
    let end = Math.min(this.lastPage, start + maxButtons - 1);

    start = Math.max(1, end - maxButtons + 1);

    return Array.from({ length: end - start + 1 }, (_, index) => {
      return start + index;
    });
  }

  t(key: LabelKey): string {
    return UI_LABELS[this.isAr ? 'ar' : 'en'][key] || key;
  }

  onTextInput(event: Event, key: FilterKey): void {
    const input = event.target as HTMLInputElement;
    this.filters[key] = input.value || '';
  }

  onSelectFilter(event: Event, key: FilterKey, applyNow = true): void {
    const select = event.target as HTMLSelectElement;
    this.filters[key] = select.value || '';

    if (applyNow) {
      this.applyFilters();
    }
  }

  onCountryChange(event: Event, applyNow = true): void {
    const select = event.target as HTMLSelectElement;

    this.filters.country_id = select.value || '';
    this.filters.city_id = '';

    if (applyNow) {
      this.applyFilters();
    }
  }

  setOptionFilter(key: 'employment_type' | 'work_model', value: string): void {
    this.filters[key] = value;
    this.applyFilters();
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.syncUrl(1);
  }

  resetFilters(): void {
    this.filters = this.createEmptyFilters();
    this.mobileFilters = { ...this.filters };

    this.currentPage = 1;
    this.syncUrl(1);
  }

  openFiltersModal(): void {
    this.mobileFilters = { ...this.filters };
    this.filtersModalOpen = true;
  }

  closeFiltersModal(): void {
    this.filtersModalOpen = false;
  }

  onMobileSelectFilter(event: Event, key: FilterKey): void {
    const select = event.target as HTMLSelectElement;
    this.mobileFilters[key] = select.value || '';
  }

  onMobileCountryChange(event: Event): void {
    const select = event.target as HTMLSelectElement;

    this.mobileFilters.country_id = select.value || '';
    this.mobileFilters.city_id = '';
  }

  setMobileOptionFilter(
    key: 'employment_type' | 'work_model',
    value: string
  ): void {
    this.mobileFilters[key] = value;
  }

  applyMobileFilters(): void {
    this.filters = { ...this.mobileFilters };
    this.filtersModalOpen = false;
    this.applyFilters();
  }

  resetMobileFilters(): void {
    this.mobileFilters = this.createEmptyFilters();
    this.filters = { ...this.mobileFilters };
    this.filtersModalOpen = false;

    this.currentPage = 1;
    this.syncUrl(1);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.lastPage || page === this.currentPage) {
      return;
    }

    this.syncUrl(page);

    if (this.isBrowser) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }

  getCountriesAndCities(): void {
    const sub = forkJoin({
      countries: this.countryCityService.getCountries({ per_page: 1000 }),
      cities: this.countryCityService.getCities({ per_page: 1000 })
    }).subscribe({
      next: (res: any) => {
        this.countries = this.extractArray(res.countries);
        this.cities = this.extractArray(res.cities);
      },
      error: (err: any) => {
        console.error('Countries/Cities loading error:', err);
        this.countries = [];
        this.cities = [];
      }
    });

    this.subscriptions.add(sub);
  }

  getJobs(page = 1): void {
    this.currentPage = page;
    this.isLoading = true;

    const sub = this.jobService
      .getJobs(this.buildRequestFilters(page))
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (res: any) => {
          const paginationData = res?.data;

          this.jobs = Array.isArray(paginationData?.data)
            ? paginationData.data
            : this.extractArray(res);

          this.totalJobsCount = Number(paginationData?.total || this.jobs.length || 0);
          this.currentPage = Number(paginationData?.current_page || page || 1);
          this.lastPage = Number(paginationData?.last_page || 1);
          this.from = Number(paginationData?.from || 0);
          this.to = Number(paginationData?.to || this.jobs.length || 0);
        },
        error: (err: any) => {
          console.error('Jobs loading error:', err);

          this.jobs = [];
          this.totalJobsCount = 0;
          this.currentPage = 1;
          this.lastPage = 1;
          this.from = 0;
          this.to = 0;
        }
      });

    this.subscriptions.add(sub);
  }

  goJobDetails(job: any): void {
    if (!job?.id) return;

    this.router.navigate(['/job', job.id]);
  }

  openJobDetails(job: any): void {
    if (!job?.id) return;

    this.selectedJob = job;
    this.modalOpen = true;
    this.isDetailsLoading = true;

    const viewsSub = this.jobService.increaseViews(job.id).subscribe({
      next: () => {},
      error: () => {}
    });

    const detailsSub = this.jobService
      .getJob(job.id)
      .pipe(
        finalize(() => {
          this.isDetailsLoading = false;
        })
      )
      .subscribe({
        next: (res: any) => {
          this.selectedJob = res?.data || job;
        },
        error: (err: any) => {
          console.error('Job details error:', err);
          this.selectedJob = job;
        }
      });

    this.subscriptions.add(viewsSub);
    this.subscriptions.add(detailsSub);
  }

  closeJobDetails(): void {
    this.modalOpen = false;
    this.selectedJob = null;
    this.isDetailsLoading = false;
  }

  applyForJob(job: any): void {
    if (this.isCompanyUser()) {
      return;
    }

    if (!job) return;

    if (this.isJobApplied(job)) {
      return;
    }

    if (this.getApplicationFormType(job) === 'doctor') {
      this.router.navigate(['/apply/doctor', job.id]);
      return;
    }

    if (this.getApplicationFormType(job) === 'dentist') {
      this.router.navigate(['/apply/dentist', job.id]);
      return;
    }

    if (this.getApplicationFormType(job) === 'general' && this.tryDirectApply(job)) {
      return;
    }

    this.selectedApplyJob = job;
    this.applyModalOpen = true;

    this.modalOpen = false;
    this.isDetailsLoading = false;
  }

  closeApplyModal(): void {
    this.applyModalOpen = false;
    this.selectedApplyJob = null;
  }

  handleApplicationSubmitted(): void {
    if (this.selectedApplyJob?.id) {
      this.appliedJobIds.add(String(this.selectedApplyJob.id));
    }

    this.closeApplyModal();
  }

  isJobApplied(job: any): boolean {
    return Boolean(job?.id && this.appliedJobIds.has(String(job.id)));
  }

  isCompanyUser(): boolean {
    if (!this.isBrowser) {
      return false;
    }

    const directType =
      localStorage.getItem('user_type') ||
      localStorage.getItem('account_type') ||
      localStorage.getItem('role') ||
      localStorage.getItem('user_role') ||
      localStorage.getItem('type');

    if (this.normalizeUserType(directType) === 'company') {
      return true;
    }

    try {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;

      return this.normalizeUserType(
        user?.user_type ||
        user?.account_type ||
        user?.role ||
        user?.user_role ||
        user?.type ||
        user?.user?.user_type
      ) === 'company';
    } catch {
      return false;
    }
  }

  getApplyButtonLabel(job: any): string {
    if (this.isJobApplied(job)) {
      return this.isAr ? 'تم التقديم' : 'Applied';
    }

    return this.t('apply');
  }

  getApplyNowButtonLabel(job: any): string {
    if (this.isJobApplied(job)) {
      return this.isAr ? 'تم التقديم' : 'Applied';
    }

    return this.t('applyNow');
  }

  shareSearch(): void {
    if (!this.isBrowser) return;

    const url = new URL(window.location.href);
    const params = new URLSearchParams();

    Object.entries(this.buildUrlQueryParams(this.currentPage)).forEach(
      ([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.set(key, String(value));
        }
      }
    );

    url.search = params.toString();

    if (navigator.clipboard) {
      navigator.clipboard.writeText(url.toString()).then(() => {
        this.showShareMessage();
      });
    } else {
      this.showShareMessage();
    }
  }

  getJobTitle(job: any): string {
    return this.pickLang(
      job,
      'title_ar',
      'title_en',
      'title',
      this.t('untitledJob')
    );
  }

  getCompanyName(job: any): string {
    // Employer identities are intentionally private on public job cards.
    return this.t('confidentialEmployer');
  }

  getCountryName(job: any): string {
    if (!job) return this.emptyText();

    if (job?.country && typeof job.country === 'object') {
      return this.pickLang(
        job.country,
        'country_name_ar',
        'country_name_en'
      );
    }

    if (job?.country_name_ar || job?.country_name_en) {
      return this.pickLang(job, 'country_name_ar', 'country_name_en');
    }

    const countryValue = job?.country_id || job?.country;

    if (!countryValue) return this.emptyText();

    const country = this.countries.find((item: any) => item.id == countryValue);

    if (country) {
      return this.getCountryLabel(country);
    }

    if (typeof countryValue === 'string' && isNaN(Number(countryValue))) {
      return countryValue;
    }

    return this.emptyText();
  }

  getCityName(job: any): string {
    if (!job) return this.emptyText();

    if (job?.city && typeof job.city === 'object') {
      return this.pickLang(job.city, 'city_name_ar', 'city_name_en');
    }

    if (job?.city_name_ar || job?.city_name_en) {
      return this.pickLang(job, 'city_name_ar', 'city_name_en');
    }

    const cityValue = job?.city_id || job?.city;

    if (!cityValue) return this.emptyText();

    const city = this.cities.find((item: any) => item.id == cityValue);

    if (city) {
      return this.getCityLabel(city);
    }

    if (typeof cityValue === 'string' && isNaN(Number(cityValue))) {
      return cityValue;
    }

    return this.emptyText();
  }

  getCountryLabel(country: any): string {
    return this.pickLang(country, 'country_name_ar', 'country_name_en');
  }

  getCityLabel(city: any): string {
    return this.pickLang(city, 'city_name_ar', 'city_name_en');
  }

  getJobType(job: any): string {
    return this.getEmploymentTypeLabel(job?.employment_type || job?.type);
  }

  getApplicationFormType(job: any): 'general' | 'doctor' | 'dentist' {
    const jobType = String(job?.job_type || '').trim().toLowerCase();

    if (jobType === 'fordoctors') return 'doctor';
    if (jobType === 'fordentist') return 'dentist';

    return 'general';
  }

  getWorkModel(job: any): string {
    return this.getWorkModelLabel(job?.work_model);
  }

  getWorkModelLabel(value: string): string {
    const normalized = value || '';

    const ar: any = {
      on_site: 'من مقر العمل',
      onsite: 'من مقر العمل',
      remote: 'عن بُعد',
      hybrid: 'هايبرد'
    };

    const en: any = {
      on_site: 'On site',
      onsite: 'On site',
      remote: 'Remote',
      hybrid: 'Hybrid'
    };

    return this.isAr
      ? ar[normalized] || this.emptyText()
      : en[normalized] || this.emptyText();
  }

  getEmploymentTypeLabel(value: string): string {
    const normalized = value || '';

    const ar: any = {
      full_time: 'دوام كامل',
      'full-time': 'دوام كامل',
      part_time: 'دوام جزئي',
      'part-time': 'دوام جزئي',
      contract: 'تعاقد',
      internship: 'تدريب'
    };

    const en: any = {
      full_time: 'Full time',
      'full-time': 'Full time',
      part_time: 'Part time',
      'part-time': 'Part time',
      contract: 'Contract',
      internship: 'Internship'
    };

    return this.isAr
      ? ar[normalized] || this.formatText(normalized)
      : en[normalized] || this.formatText(normalized);
  }

  getSalary(job: any): string {
    if (!job) return this.emptyText();

    const salaryNote = this.pickLang(
      job,
      'salary_note_ar',
      'salary_note_en',
      'salary_note',
      ''
    );

    if (salaryNote) return salaryNote;

    if (job?.salary_type === 'competitive') {
      return this.t('competitiveSalary');
    }

    if (job?.salary_type === 'negotiable') {
      return this.t('negotiable');
    }

    const currency = job?.salary_currency || '';

    const hasMin = this.hasValue(job?.salary_min);
    const hasMax = this.hasValue(job?.salary_max);

    if (hasMin && hasMax) {
      return `${this.formatNumber(job.salary_min)} - ${this.formatNumber(
        job.salary_max
      )} ${currency}`.trim();
    }

    if (hasMin) {
      return `${this.t('from')} ${this.formatNumber(job.salary_min)} ${currency}`.trim();
    }

    if (hasMax) {
      return `${this.t('upTo')} ${this.formatNumber(job.salary_max)} ${currency}`.trim();
    }

    return this.t('salaryNotMentioned');
  }

  getPostedAt(job: any): string {
    const dateValue = job?.posted_at || job?.created_at;

    if (!dateValue) return this.t('recently');

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return this.t('recently');
    }

    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return this.t('recently');

    if (diffDays === 1) return this.t('yesterday');

    if (diffDays < 30) {
      return this.isAr
        ? `منذ ${this.formatNumber(diffDays)} ${this.t('daysAgo')}`
        : `${this.formatNumber(diffDays)} ${this.t('daysAgo')}`;
    }

    return date.toLocaleDateString(this.isAr ? 'ar-EG' : 'en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  getTags(job: any, limit = 0): string[] {
    if (!Array.isArray(job?.skills) || !job.skills.length) {
      return [];
    }

    const tags = job.skills
      .map((skill: any) => {
        if (typeof skill === 'string') return skill;

        return this.pickLang(skill, 'name_ar', 'name_en', 'name', '');
      })
      .filter(Boolean);

    return limit ? tags.slice(0, limit) : tags;
  }

  getRequirements(job: any): any[] {
    return Array.isArray(job?.requirements) ? job.requirements : [];
  }

  getJobDescription(job: any): string {
    return this.pickLang(
      job,
      'description_ar',
      'description_en',
      'description',
      ''
    );
  }

  getJobResponsibilities(job: any): string {
    return this.pickLang(
      job,
      'responsibilities_ar',
      'responsibilities_en',
      'responsibilities',
      ''
    );
  }

  getRequirementText(req: any): string {
    return this.pickLang(
      req,
      'requirement_text_ar',
      'requirement_text_en',
      'requirement_text',
      ''
    );
  }

  getRequirementTypeLabel(value: any): string {
    if (!value) return this.emptyText();

    const normalized = String(value);

    const ar: any = {
      education: 'التعليم',
      experience: 'الخبرة',
      certificate: 'شهادة',
      language: 'لغة',
      other: 'أخرى'
    };

    const en: any = {
      education: 'Education',
      experience: 'Experience',
      certificate: 'Certificate',
      language: 'Language',
      other: 'Other'
    };

    return this.isAr
      ? ar[normalized] || this.formatText(normalized)
      : en[normalized] || this.formatText(normalized);
  }

  getStatusLabel(value: string): string {
    const normalized = value || 'published';

    const ar: any = {
      draft: this.t('draft'),
      published: this.t('published'),
      closed: this.t('closed')
    };

    const en: any = {
      draft: this.t('draft'),
      published: this.t('published'),
      closed: this.t('closed')
    };

    return this.isAr
      ? ar[normalized] || this.formatText(normalized)
      : en[normalized] || this.formatText(normalized);
  }

  formatNumber(value: any): string {
    if (value === null || value === undefined || value === '') return '';

    const numberValue = Number(value);

    if (Number.isFinite(numberValue)) {
      return new Intl.NumberFormat(this.isAr ? 'ar-EG' : 'en-US', {
        maximumFractionDigits: 0
      }).format(numberValue);
    }

    const stringValue = String(value);

    if (!this.isAr) return stringValue;

    return stringValue.replace(/\d/g, (digit: string) => {
      return '٠١٢٣٤٥٦٧٨٩'[Number(digit)];
    });
  }

  formatText(value: any): string {
    if (!value) return this.emptyText();

    return String(value)
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  trackByJobId(index: number, job: any): number {
    return job?.id || index;
  }

  trackByRequirementId(index: number, req: any): number {
    return req?.id || index;
  }

  trackById(index: number, item: any): number {
    return item?.id || index;
  }

  private hydrateFiltersFromQuery(queryParams: ParamMap): void {
    this.filters.search = queryParams.get('search') || '';
    this.filters.country_id = queryParams.get('country_id') || '';
    this.filters.city_id = queryParams.get('city_id') || '';
    this.filters.work_model = queryParams.get('work_model') || '';
    this.filters.employment_type = queryParams.get('employment_type') || '';
    this.mobileFilters = { ...this.filters };

    const page = Number(queryParams.get('page') || 1);

    this.currentPage = Number.isFinite(page) && page > 0 ? page : 1;
  }

  private syncUrl(page: number): void {
    if (!this.isBrowser) return;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.buildUrlQueryParams(page),
      replaceUrl: true
    });
  }

  private buildUrlQueryParams(page: number): any {
    const queryParams: any = {};

    Object.entries(this.filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        queryParams[key] = String(value).trim();
      }
    });

    if (page > 1) {
      queryParams.page = page;
    }

    return queryParams;
  }

  private buildRequestFilters(page: number): any {
    return {
      status: 'published',
      per_page: this.perPage,
      page,
      ...this.cleanFilters(this.filters)
    };
  }

  private cleanFilters(filters: JobsFilters): any {
    const cleaned: any = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        cleaned[key] = String(value).trim();
      }
    });

    return cleaned;
  }

  private hasAnyFilter(filters: JobsFilters): boolean {
    return Object.values(filters).some(value => {
      return value !== null && value !== undefined && String(value).trim() !== '';
    });
  }

  private createEmptyFilters(): JobsFilters {
    return {
      search: '',
      country_id: '',
      city_id: '',
      work_model: '',
      employment_type: ''
    };
  }

  private extractArray(res: any): any[] {
    if (Array.isArray(res)) {
      return res;
    }

    if (Array.isArray(res?.data?.data)) {
      return res.data.data;
    }

    if (Array.isArray(res?.data)) {
      return res.data;
    }

    return [];
  }

  private getCurrentLang(): string {
    if (!this.isBrowser) return 'en';

    return (
      localStorage.getItem('language') ||
      localStorage.getItem('lang') ||
      localStorage.getItem('translocoLang') ||
      document.documentElement.lang ||
      'en'
    ).toLowerCase();
  }

  private pickLang(
    item: any,
    arKey: string,
    enKey: string,
    fallbackKey?: string,
    fallback: string = this.emptyText()
  ): string {
    if (!item) return fallback;

    if (this.isAr) {
      return (
        item?.[arKey] ||
        item?.[enKey] ||
        (fallbackKey ? item?.[fallbackKey] : '') ||
        fallback
      );
    }

    return (
      item?.[enKey] ||
      item?.[arKey] ||
      (fallbackKey ? item?.[fallbackKey] : '') ||
      fallback
    );
  }

  private emptyText(): string {
    return this.t('notSpecified');
  }

  private hasValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  private tryDirectApply(job: any): boolean {
    const user = this.getStoredUserWithCv();

    if (!user?.id || !job?.id) {
      return false;
    }

    if (this.directApplyingJobId) {
      return true;
    }

    this.directApplyingJobId = job.id;

    const sub = this.applicationService
      .applyToJob({
        job_id: job.id,
        user_id: user.id
      })
      .pipe(finalize(() => (this.directApplyingJobId = null)))
      .subscribe({
        next: () => {
          this.appliedJobIds.add(String(job.id));
          this.applyModalOpen = false;
          this.selectedApplyJob = null;
          this.modalOpen = false;
          this.isDetailsLoading = false;
          this.showInlineMessage(this.directApplySuccessText());
        },
        error: (err: any) => {
          if (this.isAlreadyAppliedError(err)) {
            this.appliedJobIds.add(String(job.id));
          }

          this.showInlineMessage(this.getDirectApplyErrorText(err));
        }
      });

    this.subscriptions.add(sub);
    return true;
  }

  private getStoredUserWithCv(): any | null {
    if (this.isCompanyUser()) {
      return null;
    }

    if (!this.isBrowser) {
      return null;
    }

    try {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : {};
      const userId = user?.id || localStorage.getItem('user_id');
      const cv =
        user?.user_cv ||
        user?.user_cv_url ||
        user?.cv ||
        localStorage.getItem('user_cv') ||
        localStorage.getItem('user_cv_url');

      return userId && cv ? { ...user, id: userId, user_cv: cv } : null;
    } catch {
      return null;
    }
  }

  private loadAppliedJobIds(): void {
    const userId = this.getStoredUserId();

    if (!userId) {
      return;
    }

    const sub = this.applicationService.getApplicationsByUserId(userId).subscribe({
      next: (res: any) => {
        this.appliedJobIds = new Set(
          this.extractArray(res?.data || res)
            .map((application: any) => this.getApplicationJobId(application))
            .filter((jobId: any) => jobId !== null && jobId !== undefined)
            .map((jobId: any) => String(jobId))
        );
      },
      error: () => {
        this.appliedJobIds.clear();
      }
    });

    this.subscriptions.add(sub);
  }

  private getStoredUserId(): number | string | null {
    if (!this.isBrowser) {
      return null;
    }

    try {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : {};

      return user?.id || localStorage.getItem('user_id') || null;
    } catch {
      return localStorage.getItem('user_id') || null;
    }
  }

  private getApplicationJobId(application: any): number | string | null {
    return (
      application?.job_id ||
      application?.job?.id ||
      application?.ahmed_elshahat_job?.id ||
      application?.job_data?.id ||
      null
    );
  }

  private directApplySuccessText(): string {
    return this.isAr
      ? 'تم التقديم على الوظيفة بنجاح'
      : 'Application submitted successfully';
  }

  private getDirectApplyErrorText(err: any): string {
    const message =
      err?.error?.message ||
      err?.error?.error ||
      (err?.status === 409 || err?.status === 422
        ? this.isAr
          ? 'لقد قدمت على هذه الوظيفة من قبل'
          : 'You have already applied for this job'
        : '');

    return (
      message ||
      (this.isAr
        ? 'تعذر التقديم الآن. حاول مرة أخرى.'
        : 'Could not apply right now. Please try again.')
    );
  }

  private isAlreadyAppliedError(err: any): boolean {
    return err?.status === 409 || err?.status === 422;
  }

  private normalizeUserType(value: any): string {
    const type = String(value || '').toLowerCase().trim();

    if (
      type === 'company' ||
      type === 'companies' ||
      type === 'employer' ||
      type === 'employers' ||
      type === 'business'
    ) {
      return 'company';
    }

    return type;
  }

  private showShareMessage(): void {
    this.showInlineMessage(this.t('copied'));
  }

  private showInlineMessage(message: string): void {
    this.shareMessage = message;

    setTimeout(() => {
      this.shareMessage = '';
    }, 1800);
  }
}
